if (!customElements.get('ugc-video-pair')) {
  class UgcVideoPair extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this._init = false;
      this.queueInit();
    }

    disconnectedCallback() {
      this._init = false;
      if (this._visFrame) cancelAnimationFrame(this._visFrame);
      this.controller?.abort();
      this.observer?.disconnect();
    }

    queueInit() {
      const run = () => {
        if (!this.isConnected || this._init) return;
        if (!this.querySelector('[data-ugc-item]')) return;
        this._init = true;
        this.setup();
      };

      run();
      if (!this._init) {
        requestAnimationFrame(() => {
          run();
          if (!this._init) requestAnimationFrame(run);
        });
      }
    }

    setup() {
      this.items = Array.from(this.querySelectorAll('[data-ugc-item]'));
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.controller = new AbortController();
      const { signal } = this.controller;

      if (this.hintDismissed()) this.classList.add('is-hint-dismissed');

      this.items.forEach((item) => {
        const video = item.querySelector('video');
        const button = item.querySelector('.ugc-pair__hit');
        if (!video || !button) return;

        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.controls = false;
        video.disablePictureInPicture = true;
        if (!item.classList.contains('is-audible')) {
          video.muted = true;
          video.defaultMuted = true;
        }

        button.addEventListener('click', () => this.onPress(item), { signal });
        video.addEventListener(
          'playing',
          () => {
            item.classList.remove('is-blocked');
          },
          { signal }
        );
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            video._ugcInView = entry.isIntersecting;
            if (!entry.isIntersecting) {
              video.pause();
              return;
            }
            this.onVisible(video);
          });
        },
        { threshold: 0.35, rootMargin: '160px 0px' }
      );

      this.items.forEach((item) => {
        const video = item.querySelector('video');
        if (video) this.observer.observe(video);
      });

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.items.forEach((item) => item.querySelector('video')?.pause());
            return;
          }
          this.syncVisibility();
        },
        { signal }
      );

      this.scheduleVisibilityCheck = () => {
        if (this._visFrame) return;
        this._visFrame = requestAnimationFrame(() => {
          this._visFrame = 0;
          this.syncVisibility();
        });
      };

      document.addEventListener('scroll', this.scheduleVisibilityCheck, { signal, passive: true, capture: true });
      window.addEventListener('resize', this.scheduleVisibilityCheck, { signal, passive: true });
    }

    syncVisibility() {
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;
      this.items.forEach((item) => {
        const video = item.querySelector('video');
        if (!video) return;
        const rect = video.getBoundingClientRect();
        if (!rect.height) return;
        const visible = Math.min(rect.bottom, viewHeight) - Math.max(rect.top, 0);
        const inView = visible / rect.height >= 0.35;
        video._ugcInView = inView;
        if (inView) this.onVisible(video);
        else video.pause();
      });
    }

    onVisible(video) {
      const item = video.closest('[data-ugc-item]');
      if (this.reduceMotion && !this.userStarted) {
        item?.classList.add('is-blocked');
        return;
      }
      if (video.preload === 'none') video.preload = 'auto';
      this.play(video);
    }

    onPress(item) {
      const video = item.querySelector('video');
      if (!video) return;

      const turningOn = video.muted;
      this.userStarted = true;
      this.dismissHint();

      this.items.forEach((other) => {
        const otherVideo = other.querySelector('video');
        if (!otherVideo) return;

        const audible = other === item && turningOn;
        otherVideo.muted = !audible;
        other.classList.toggle('is-audible', audible);
        this.syncButton(other);
        if (otherVideo.preload === 'none') otherVideo.preload = 'auto';
        this.play(otherVideo);
      });
    }

    play(video) {
      const pending = video.play();
      if (!pending || typeof pending.catch !== 'function') return;

      pending.catch(() => {
        const item = video.closest('[data-ugc-item]');
        if (!video.muted) {
          video.muted = true;
          item?.classList.remove('is-audible');
          if (item) this.syncButton(item);
          video.play().catch(() => item?.classList.add('is-blocked'));
          return;
        }
        item?.classList.add('is-blocked');
      });
    }

    syncButton(item) {
      const button = item.querySelector('.ugc-pair__hit');
      if (!button) return;
      const audible = item.classList.contains('is-audible');
      button.setAttribute('aria-pressed', audible ? 'true' : 'false');
      button.setAttribute('aria-label', audible ? 'Turn sound off' : 'Turn sound on');
    }

    hintDismissed() {
      try {
        return sessionStorage.getItem('ugc-pair-sound-hint') === '1';
      } catch (error) {
        return false;
      }
    }

    dismissHint() {
      this.classList.add('is-hint-dismissed');
      try {
        sessionStorage.setItem('ugc-pair-sound-hint', '1');
      } catch (error) {
        /* private browsing */
      }
    }
  }

  customElements.define('ugc-video-pair', UgcVideoPair);
}
